import { type FC } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { PrescriptionPrescriptionTypeEnum } from '@redteclab/api/clients/bully'
import { type CrossSellProduct } from '@redteclab/api/clients/retail-media-xsell-service'

import { Button, Skeleton } from 'base-ui'

import { crossSellAccessorIsSellerShopApotheke } from '../../../cross-sell/model/crossSellAccessorIsSellerShopApotheke'
import { crossSellAccessorIsFreeShipping } from '../../../cross-sell/model/crossSellAccessorIsShippingPriceFree'
import { useGlobalConfigContext } from '../../../global-config/context/globalConfigContext'
import { priceGetFormattedPrice } from '../../../price/priceGetFormattedPrice'
import { type ProductAddToCartCswCtaParams } from '../../../product-page/product-add-to-cart-params/productAddToCartParams'
import { urlResolverGetCartIntermediatePage } from '../../../url-handling/urlResolver'
import {
  EXPERIMENTS_TRACK_EVENT_CTA_BUTTON_ON_ALL_CSW,
  experimentsTrackEvent,
} from '../../eventTracking'

import { ExperimentCtaButtonOnAllCswProductBuyBoxSellerInfo } from './ExperimentCtaButtonOnAllCswProductBuyBoxSellerInfo'
import { useShowExperimentCtaButton } from './useShowExperimentCtaButton'

const useGetCswAddToCartUrl = (crossSellProduct?: CrossSellProduct): string => {
  const globalConfig = useGlobalConfigContext()

  if (!crossSellProduct) {
    return ''
  }

  const queryParams: ProductAddToCartCswCtaParams = {
    cartAction: 'add',
    eventType: 'conversion',
    offer: crossSellProduct.offerId,
    prescriptionType: PrescriptionPrescriptionTypeEnum.NoPrescription,
    product: crossSellProduct.id,
    quantity: 1,
    redirectToCart: false,
  }

  return urlResolverGetCartIntermediatePage(globalConfig, queryParams)
}

export const ExperimentCtaButtonOnAllCsw: FC<{
  crossSellProduct?: CrossSellProduct & {
    offer_shipping_price?: number
    shippingPrice?: number
  }
}> = ({ crossSellProduct }) => {
  const intl = useIntl()
  const config = useGlobalConfigContext()
  const showButton = useShowExperimentCtaButton()
  const addToCartCswUrl = useGetCswAddToCartUrl(crossSellProduct)

  if (!showButton) {
    return null
  }

  if (!crossSellProduct) {
    return (
      <div className="flex flex-col items-center">
        <Skeleton className="h-[var(--button-height)] w-full" />
      </div>
    )
  }

  const getShippingPrice = (): number => {
    if (crossSellProduct.offer_shipping_price) {
      return Number.parseFloat(
        String(crossSellProduct.offer_shipping_price).replace(',', '.'),
      )
    }

    if (crossSellProduct.shippingPrice) {
      return Number.parseFloat(
        String(crossSellProduct.shippingPrice).replace(',', '.'),
      )
    }

    return 0
  }

  const shippingPrice = getShippingPrice()

  const priceFormatted = priceGetFormattedPrice(shippingPrice, intl)

  /**c
   * To determine, if the product has a Mirakl offer or a SAE for crosssell widgets,
   * it is enough to know that the seller name is not a shop-apotheke. All other information
   * is filtered before, in the crosssell widgets provider.
   */

  const isShippingPriceFree = crossSellAccessorIsFreeShipping(
    String(shippingPrice),
  )

  const shouldRenderSellerBox = !crossSellAccessorIsSellerShopApotheke(
    crossSellProduct.sellerId,
  )

  return (
    <div>
      <Button<'a'>
        className="w-full"
        data-qa-id="add-to-cart-button"
        href={addToCartCswUrl}
        onClick={() => {
          experimentsTrackEvent(
            config,
            EXPERIMENTS_TRACK_EVENT_CTA_BUTTON_ON_ALL_CSW,
          )
        }}
        size="small"
        slots={{ root: 'a' }}
        variant="outlined"
      >
        <FormattedMessage id="general.button.label.addToCart" />
      </Button>
      <div>
        {isShippingPriceFree ? (
          <div className="px-2 pt-2 text-center text-xs text-dark-hover-success-medium">
            <FormattedMessage id="general.label.now.freeShipping" />
          </div>
        ) : (
          <div className="px-2 pt-2 text-center text-xs text-dark-primary-low">
            <FormattedMessage
              id="general.label.additionalShippingCost"
              values={{ amount: priceFormatted }}
            />
          </div>
        )}
        {shouldRenderSellerBox ? (
          <ExperimentCtaButtonOnAllCswProductBuyBoxSellerInfo
            crossSellProduct={crossSellProduct}
          />
        ) : null}
      </div>
    </div>
  )
}
